import React, { useEffect, useRef, useState } from "react";
import Card from "components/card";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { IoEye } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import Pagination from "components/pagination/Pagination";
import Loader from "../../../assets/img/loader.gif";
import Toast from "components/toaster";
import moment from "moment";
import { FiSearch } from "react-icons/fi";
import Select from "react-select";
import Details from "./Details";
import UserService from "services/user.service";
import tabCache from "utils/tabCache";

const columnHelper = createColumnHelper();

const Reports = () => {
  const navigate = useNavigate();
  const [sorting, setSorting] = React.useState([]);
  // Initialize from cache if available
  const [data, setData] = useState(tabCache.reports.data || []);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  // Initialize from cache - show loader only on first load
  const [isLoading, setIsLoading] = useState(false);
  const [reportType, setReportType] = useState("");
  const [searchText, setSearchText] = useState("");
  const [viewOpen, setViewOpen] = useState(null);
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [isFilterChanging, setIsFilterChanging] = useState(false);


  const prevSearchTextRef = useRef("");


  const getUser = async (isSearching = false) => {

    const isFirstLoad = tabCache.reports.firstLoad;
    
    // Show loader only on first load
    if (isFirstLoad || isSearching || isPageChanging || isFilterChanging) {
      setIsLoading(true);
    }
    
    
    try {
      const body = {
        pagination: {
          page: page,
          pageSize: pageSize,
          projection: {},
          filters: {
            report_type: reportType === "" ? [] : [reportType], // post, account
          },
          sort: {},
        },
        search: encodeURIComponent(searchText),
      };
      const response = await UserService.getReports(body);
      const reportsData = response.data.data;
      
      // Update state and cache
      setData(reportsData);
      tabCache.reports.data = reportsData;
      setTotalPages(Math.round(response.data.totalCount / pageSize));
      
      // After first API call, don't show loader next time
      if (isFirstLoad) {
        tabCache.reports.firstLoad = false;
      }
    } catch (error) {
      Toast("error", error.response.data.message);
      const emptyData = [];
      setData(emptyData);
      tabCache.reports.data = emptyData;
      if (isFirstLoad) {
        tabCache.reports.firstLoad = false;
      }
    } finally {
      if (isFirstLoad || isSearching || isPageChanging || isFilterChanging) {
        setIsLoading(false);
      }
      setIsPageChanging(false);
      setIsFilterChanging(false);
    }
    
  };

  useEffect(() => {
    const isSearchChange = prevSearchTextRef.current !== searchText;
  
    if (isSearchChange) {
      prevSearchTextRef.current = searchText;
    }
  
    getUser(isSearchChange);
    // eslint-disable-next-line
  }, [page, pageSize, searchText, reportType]);
  

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const columns = [
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Time</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <p className="text-sm font-bold text-white">
            {info.getValue()
              ? moment(info.getValue()).format("DD/MM/YYYY hh:mm A")
              : "-"}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("message", {
      id: "message",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Message</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue() ? info.getValue() : "-"}
        </p>
      ),
    }),
    columnHelper.accessor("reporter", {
      id: "reporter",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Reported by</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue()?.name ? info.getValue()?.name : "--"}
        </p>
      ),
    }),
    columnHelper.accessor("account", {
      id: "account",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Report For</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          {info.getValue()?.name ? info.getValue()?.name : "--"}
        </div>
      ),
    }),
    columnHelper.accessor("report_type", {
      id: "report_type",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Report Type</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          {info.getValue() ? info.getValue() : "--"}
        </div>
      ),
    }),
    columnHelper.accessor("action", {
      id: "action",
      header: () => (
        <p className="text-sm font-bold uppercase text-white text-center">Action</p>
      ),
      cell: (info) => {
        return (
          <div className="flex justify-center">
            <div
              className="ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[#369FFF1A]/10 text-[#369FFF] duration-150 "
              onClick={() => handleOpen(info.row.original)}
            >
              <IoEye />
            </div>
          </div>
        );
      },
    }),
  ]; // eslint-disable-next-line

  const handleOpen = async (item) => {
    setViewOpen(item);
    document.body.style.overflow = "hidden";
  };

  const handleClose = () => {
    setViewOpen(null);
    document.body.style.overflow = "unset";
  };

  // const [data, setData] = React.useState(() => [...defaultData]);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const options = [
    { label: "All", value: "" },
    { label: "Post", value: "post" },
    { label: "Account", value: "account" },
  ];

  const handleSelectReportType = async (option) => {
    if (!option || option.value === reportType) return;
    setIsFilterChanging(true);
    setReportType(option.value);
  };

  const colourStyles = {
    control: (styles, state) => ({
      ...styles,
      backgroundColor: "rgb(30 30 32 / 1)",
      border: "1px solid rgba(255, 255, 255, 0.1) !important",
      boxShadow: "none !important",
      "&:hover": {
        border: "1px solid rgba(255, 255, 255, 0.1) !important",
      },
    }),
    singleValue: (styles) => ({
      ...styles,
      color: "#ffffff",
    }),
    option: (styles) => ({
      ...styles,
      backgroundColor: "rgb(30 30 32 / 1) !important",
      ":hover": {
        backgroundColor: "rgba(255, 255, 255, 0.30) !important",
        color: "#ffffff",
      },
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: "rgb(12 14 28 / 1)",
      borderRadius: "12px",
      border: "1px solid #2A2A2A",
      overflow: "hidden",
      marginTop: "7px",
      width: "calc(100% - 10px)",
      maxWidth: "150px",
      marginLeft: "4px",
    }),
  };
  return (
    <div className="mt-3  grid h-full grid-cols-1 gap-5">
      <style>
        {`
          @media (max-width: 499px) {
            .reports-header-container {
              flex-direction: row !important;
              align-items: center !important;
              justify-content: space-between !important;
              gap: 0.5rem !important;
            }
            .reports-header-container > div:first-child {
              width: auto !important;
              flex-shrink: 1 !important;
            }
            .reports-header-container .search-input-container {
              max-width: 100px !important;
              width: 100px !important;
              min-width: 100px !important;
            }
            .reports-header-container .search-input-field {
              width: 50px !important;
            }
            .reports-header-container .default {
              width: auto !important;
              flex-shrink: 0 !important;
              justify-content: flex-end !important;
            }
          }
          @media (min-width: 500px) and (max-width: 639px) {
            .search-input-container {
              max-width: 140px !important;
            }
            .search-input-field {
              width: 100px !important;
            }
          }
          @media (min-width: 500px) and (max-width: 575px) {
            .reports-header-container {
              flex-direction: row !important;
              align-items: center !important;
              justify-content: space-between !important;
              gap: 0.5rem !important;
            }
            .reports-header-container > div:first-child {
              width: auto !important;
              flex-shrink: 1 !important;
            }
            .reports-header-container .search-input-container {
              max-width: 130px !important;
              width: 130px !important;
              min-width: 130px !important;
            }
                .search-input-field {
              width: 60px !important;
            }
            .reports-header-container .default {
              width: auto !important;
              flex-shrink: 0 !important;
              justify-content: flex-end !important;
            }
          }
        `}
      </style>
      <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
        <Card extra={"w-full h-fit pb-6 px-6 relative mt-0.3"}>
          <div className="reports-header-container flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 mt-4 -ml-2">
            <div className="flex w-full items-center sm:w-auto">
              <div className="search-input-container flex w-full items-center rounded-full bg-navy-900 py-3 text-white sm:max-w-[180px] xl:w-[180px]">
                <p className="pl-3 pr-2 text-xl">
                  <FiSearch className="h-4 w-4 text-white" />
                </p>
                <input
                  type="text"
                  placeholder="Search..."
                  className="search-input-field block w-full flex-1 bg-navy-900 text-sm font-medium max-w-[120px] text-white outline-none placeholder:!text-white"
                  onChange={handleSearch}
                  value={searchText}
                />
              </div>
            </div>
            <div className="default flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-end -mr-3">
              <div className="flex items-center sm:w-auto">
                <Select
                  className="w-full sm:w-[160px] !text-white"
                  classNamePrefix="select"
                  defaultValue={options[0]}
                  name="reportType"
                  options={options}
                  value={options.find((option) => option?.value == reportType)}
                  onChange={handleSelectReportType}
                  styles={colourStyles}
                />
              </div>
            </div>
          </div>
          <div
            className={`table-scroll mt-6 -mx-6 relative ${
              isLoading
                ? "h-[65vh] overflow-hidden"
                : "overflow-x-auto"
            }`}
          >
            {isLoading && (
              <div className="pointer-events-none absolute inset-0 z-40 hidden items-center justify-center md:flex">
                <img src={Loader} alt="Loading..." className="h-16 w-16" />
              </div>
            )}
            <table
              className={`z-10 w-full ${isLoading ? "table-fixed" : ""}`}
              style={
                isLoading
                  ? { tableLayout: "fixed", width: "100%" }
                  : { tableLayout: "auto", minWidth: "100%" }
              }
            >
              <thead>
                {!isLoading &&
                  table.getHeaderGroups().map((headerGroup) => (
                    <tr
                      key={headerGroup.id}
                      className="!border-px !border-gray-400"
                    >
                      {headerGroup.headers.map((header, index) => {
                        const isFirst = index === 0;
                        const isLast = index === headerGroup.headers.length - 1;
                        const isCreatedAt = header.id === 'createdAt';
                        return (
                          <th
                            key={header.id}
                            colSpan={header.colSpan}
                            onClick={header.column.getToggleSortingHandler()}
                            className={`cursor-pointer border-b-[1px] border-[#fbfbfb1a] pb-2 pt-4 ${isFirst ? 'pl-6 pr-12 text-start' : isLast ? 'pl-12 pr-36 text-end' : isCreatedAt ? 'pl-24 pr-12 text-start' : 'pl-14 pr-12 text-start'}`}
                          >
                            <div className="items-center justify-between text-xs text-gray-200">
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: "",
                                desc: "",
                              }[header.column.getIsSorted()] ?? null}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  ))}
              </thead>
              <tbody>
                {isLoading ? (
                  <tr className="md:hidden">
                    <td colSpan={columns.length} className="py-6">
                      <div className="flex justify-center">
                        <img src={Loader} alt="Loading..." className="h-16 w-16" />
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows?.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="py-4 text-center">
                      No data found
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => {
                    return (
                      <tr key={row.id} className="group border-b border-[#fbfbfb1a] last:border-none hover:bg-[#8E8E8E]/5 hover:!border-b-0 transition-colors cursor-pointer">
                        {row.getVisibleCells().map((cell, cellIndex) => {
                          const isFirst = cellIndex === 0;
                          const isLast = cellIndex === row.getVisibleCells().length - 1;
                          const isCreatedAt = cell.column.id === 'createdAt';
                          return (
                            <td
                              key={cell.id}
                              className={`relative min-w-[150px] border-white/0 py-3 ${isFirst ? 'pl-6 pr-12' : isLast ? 'pl-12 pr-32' : isCreatedAt ? 'pl-24 pr-24' : 'pl-14 pr-24'}`}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {table.getRowModel().rows.length > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          )}
        </Card>
      </div>
      {viewOpen && (
        <Details isOpen={viewOpen} data={viewOpen} onClose={handleClose} />
      )}
    </div>
  );
};

export default Reports;
